import { collectFiles } from '../../../../../utils/file';
import {
  CreateDataChangeRequestInput,
  FieldType,
  ObjectFieldChangesInput,
  ObjectFieldValue,
  ObjectFieldValueFragment,
} from '../../../__generated__/graphql';
import { PositionChangeData } from './components/positionChange/types';
import { SalaryChangeData } from './components/salaryChange';
import { employeeObjectApiName, RequestFormData } from './types';

export async function mapToCreateDataChangeRequestInput(
  initiatorEmployeeId: string,
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formData: RequestFormData,
): Promise<CreateDataChangeRequestInput | null> {
  if (!formData.requestFormId || !formData.employeeId) {
    return null;
  }

  const filesToCollect = formData.files || [];
  const collectedFiles = await collectFiles(filesToCollect);

  const objectFieldChanges: ObjectFieldChangesInput | null =
    await mapToObjectFieldChangesInput(employeeRecordFields, formData);
  if (!objectFieldChanges) {
    return null;
  }
  return {
    initiatorEmployeeId,
    requestFormId: formData.requestFormId,
    payload: {
      objectsFieldsChanges: [objectFieldChanges],
      changeReason: formData.reason || '',
      files: collectedFiles.map((file) => ({
        fileName: file.fileName,
        fileContentBase64: file.fileBase64,
      })),
    },
  };
}

export async function mapToObjectFieldChangesInput(
  employeeRecordFields: readonly ObjectFieldValueFragment[],
  formData: Pick<
    RequestFormData,
    'employeeId' | 'fields' | 'positionChange' | 'salaryChange'
  >,
): Promise<ObjectFieldChangesInput | null> {
  if (!formData.employeeId) {
    return null;
  }

  const objectFieldChanges: ObjectFieldChangesInput = {
    recordId: formData.employeeId,
    objectName: employeeObjectApiName,
    fieldChanges: Object.entries(formData.fields || {}).map(
      ([fieldName, newValue]) => {
        const fieldInfo = employeeRecordFields.find(
          (field) => field.fieldInfo.name === fieldName,
        );
        return mapFieldChangesToRequestPayload(fieldName, newValue, fieldInfo);
      },
    ),
    positionChange: formData.positionChange
      ? mapToPositionChangeInput(formData.positionChange)
      : null,
    salaryChange: formData.salaryChange
      ? mapToSalaryChangeInput(formData.salaryChange)
      : null,
  };
  return objectFieldChanges;
}

const mapToSalaryChangeInput = (
  salaryChange: SalaryChangeData,
): ObjectFieldChangesInput['salaryChange'] | null => {
  if (salaryChange.newValue > 0 && !salaryChange.hasPermissionIssue) {
    return {
      currentSalaryId: salaryChange.currentSalaryId ?? null,
      effectiveDate: salaryChange.effectiveDate,
      endDate: salaryChange.endDate ?? null,
      isQuickUpdate: salaryChange.isQuickUpdate,
      newValue: salaryChange.newValue,
      newFrequency: salaryChange.newFrequency ?? null,
      notes: salaryChange.notes ?? null,
    };
  }
  return null;
};

const mapToPositionChangeInput = (
  positionChange: PositionChangeData,
): ObjectFieldChangesInput['positionChange'] | null => {
  if (
    positionChange.newPositionId &&
    positionChange.currentPositionId &&
    positionChange.effectiveDate
  ) {
    return {
      newPositionId: positionChange.newPositionId,
      currentPositionId: positionChange.currentPositionId,
      effectiveDate: positionChange.effectiveDate,
      endDate: positionChange.endDate || null,
    };
  }
  return null;
};

const mapFieldChangesToRequestPayload = (
  fieldName: string,
  newValue: any,
  fieldInfo: ObjectFieldValue | undefined,
) => {
  let newFieldValue = newValue;

  if (fieldInfo?.fieldInfo.type === FieldType.Boolean) {
    newFieldValue = newValue.toString();
  }
  if (fieldInfo?.fieldInfo.type === FieldType.Date) {
    const date = new Date(newValue);
    newFieldValue = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
      .toISOString()
      .split('T')[0];
  }

  return {
    fieldName,
    fieldType: fieldInfo?.fieldInfo.type || 'String',
    newValue: newFieldValue,
  };
};
